import { useState, useEffect } from "react";
import { Amplify, API, Auth, Storage } from "aws-amplify";
import config from "./aws-exports";
import {
    Authenticator,
    useAuthenticator,
    Loader,
    ThemeProvider,
} from "@aws-amplify/ui-react";
import { SimpleLayout } from "./ui-components/layout/SimpleLayout.js";
import { Layout } from "./ui-components/layout/Layout.js";
import {
    BrowserRouter,
    Routes,
    Route,
    Outlet,
    Navigate,
} from "react-router-dom";
import { RequireAuth } from "./RequireAuth";
import { Register } from "./ui-components/Register";
import { NewTargetedCampaign } from "./ui-components/NewTargetedCampaign";
import { UpdateTargetedCampaign } from "./ui-components/UpdateTargetedCampaign";
import { NewTestCampaign } from "./ui-components/NewTestCampaign";
import { Analytics } from "./ui-components/Analytics";
import { Campaigns } from "./ui-components/Campaigns";
import { Listings } from "./ui-components/Listings";
import { ListingForm } from "./ui-components/ListingForm";
import { BrandCookbook } from "./ui-components/BrandCookbook";
import { InfluencerCookbook } from "./ui-components/InfluencerCookbook";
import { CampaignHistory } from "./ui-components/CampaignHistory";
import { Billing } from "./ui-components/billing/Billing";
import { Earnings } from "./ui-components/Earnings";
import { AddProfile } from "./ui-components/AddProfile";
import { Dashboard } from "./ui-components/Dashboard";
import { Settings } from "./ui-components/Settings";
import { SpecificProfile } from "./ui-components/SpecificProfile";
import { MarketingReport } from "./ui-components/MarketingReport";
import { UsersReport } from "./ui-components/UsersReport";
import { CampaignsReport } from "./ui-components/CampaignsReport";
import { PayoutsReport } from "./ui-components/PayoutsReport";
import { Tasks } from "./ui-components/Tasks";
import { MostActiveUsersReport } from './ui-components/MostActiveUsersReport';
import { MostActivePlatformUsersReport } from './ui-components/MostActivePlatformUsersReport';

Amplify.configure(config);

const theme = {
    name: "influencerTheme",
    tokens: {
        colors: {
            brand: {
                primary: {
                    10: {
                        value: "hsl(210, 61%, 70%)",
                    },
                    20: {
                        value: "hsl(210, 60%, 64%)",
                    },
                    40: {
                        value: "hsl(210, 60%, 52%)",
                    },
                    60: {
                        value: "hsl(210, 90%, 40%)",
                    },
                    80: {
                        value: "hsl(210, 90%, 32%)",
                    },
                    90: {
                        value: "hsl(210, 90%, 24%)",
                    },
                    100: {
                        value: "hsl(210, 90%, 20%)",
                    },
                },
                secondary: {
                    10: {
                        value: "{colors.neutral.10.value}",
                    },
                    20: {
                        value: "{colors.neutral.20.value}",
                    },
                    40: {
                        value: "{colors.neutral.40.value}",
                    },
                    60: {
                        value: "{colors.neutral.60.value}",
                    },
                    80: {
                        value: "{colors.neutral.80.value}",
                    },
                    90: {
                        value: "{colors.neutral.90.value}",
                    },
                    100: {
                        value: "{colors.neutral.100.value}",
                    },
                },
            },
        },
        // iOS Safari zooms the whole page in when a control with a computed
        // font-size below 16px receives focus, and the app runs as a standalone
        // PWA with no way to zoom back out -- see
        // docs/mobile-layout-and-viewport.md. billing.css puts the root at 14px
        // on phones, so every rem-sized Amplify field lands under the threshold.
        //
        // This has to live in the theme, not in a stylesheet: ThemeProvider
        // injects a generated <style id="amplify-theme-influencerTheme"> AFTER
        // every static sheet, and its [data-amplify-theme="influencerTheme"]
        // selector (0,2,0) re-declares these tokens, so any :root or
        // [data-amplify-theme] rule in index.css loses on both order and
        // specificity. Flooring here means Amplify's own size variants keep
        // working off the tokens: large stays 1.25rem/17.5px, small is lifted
        // from 0.875rem/12.25px to 16px.
        //
        // Values are raw var() on purpose. Amplify's cssValue() treats a string
        // containing `{...}` as ONE whole token reference, so the idiomatic
        // "max(16px, {fontSizes.medium.value})" would be mangled into a single
        // bogus custom property name.
        components: {
            fieldcontrol: {
                fontSize: {
                    value: "max(16px, var(--amplify-font-sizes-medium))",
                },
                small: {
                    fontSize: {
                        value: "max(16px, var(--amplify-font-sizes-small))",
                    },
                },
                large: {
                    fontSize: {
                        value: "max(16px, var(--amplify-font-sizes-large))",
                    },
                },
            },
        },
    },
};

function MyRoutes() {
    const [isLoading, setLoading] = useState(true);

    const [balance, setBalance] = useState(0);
    const [profiles, setProfiles] = useState([]);
    const [accessLevel, setAccess] = useState("user");
    const [subscription, setSubscription] = useState(false);

    const [activeProfile, setActiveProfile] = useState({});

    const { user } = useAuthenticator((context) => [context.user]);

    const [clientData, setClientData] = useState({});

    async function getBalance() {
        var userLocal = user; //undefined;

        try {
            userLocal = await Auth.currentAuthenticatedUser();
        } catch (error) {
            userLocal = user;
        }

        //when just signed out both user & userLocal are undefined
        try {
            const requestBalance = {
                headers: {
                    Authorization: `${userLocal.signInUserSession.idToken.jwtToken}`,
                },
            };
            const data = await API.get(
                "gcAPI",
                `/get-client/${userLocal.username}`,
                requestBalance
            );
            //console.log('data', data);//payments "inactive"
            if (data.accessLevel && data.accessLevel === "admin")
                setAccess(data.accessLevel);
            if (data.payments && data.payments === "active")
                setSubscription(true);
            setBalance(data.starsBalance);
            if (data.profiles.length > 0) {
                var sortedProfiles = data.profiles.sort(
                    (a, b) => Number(b.validated) - Number(a.validated)
                );
                //use to refresh the token bafore refresh the pic signed url
                await Auth.currentAuthenticatedUser();
                setProfiles(
                    await Promise.all(
                        sortedProfiles.map(async (el) => {
                            el.avatarUrl = await Storage.get(el.avatarUrl, {
                                level: "protected",
                                identityId: userLocal.username,
                            });
                            return { ...el };
                        })
                    )
                );
            } else {
                setProfiles([]);
            }

            //set subscriptionPlan, payments, postBalance?, email, fiatBalance, fiatCurrency
            let clientDataLocal = {};
            if (data.subscriptionPlan)
                clientDataLocal.subscriptionPlan = data.subscriptionPlan;
            if (data.payments) clientDataLocal.payments = data.payments;
            if (data.postBalance)
                clientDataLocal.postBalance = data.postBalance;
            if (data.email) clientDataLocal.email = data.email;
            if (data.fiatBalance)
                clientDataLocal.fiatBalance = data.fiatBalance;
            if (data.fiatCurrency)
                clientDataLocal.fiatCurrency = data.fiatCurrency;
            if (data.subscriptionId)
                clientDataLocal.subscriptionId = data.subscriptionId;
            if (data.customerId) clientDataLocal.customerId = data.customerId;
            if (data.lifetimePayouts)
                clientDataLocal.lifetimePayouts = data.lifetimePayouts;
            if (data.comments) clientDataLocal.comments = data.comments;
            if (data.intros) clientDataLocal.intros = data.intros;
            if (data.pods) clientDataLocal.pods = data.pods;

            // Star ledger, computed server-side and shipped whole.
            // `spendableStars` is the ONLY number anything is allowed to spend against.
            // It is deliberately NOT derived on the client: binding the campaign stepper's
            // max to a locally-mutated balance is what let the UI offer stars the server
            // did not believe existed.
            // See docs/free-campaign-stars-and-validation.md
            clientDataLocal.starsPending = data.starsPending || 0;
            clientDataLocal.spendableStars = data.spendableStars || 0;
            clientDataLocal.starsUnlocked = !!data.starsUnlocked;
            clientDataLocal.starsUnlockReason = data.starsUnlockReason || "locked";
            clientDataLocal.starsUnlockStreakDays = data.starsUnlockStreakDays || 5;
            clientDataLocal.activityStreakBestEver = data.activityStreakBestEver || 0;
            setClientData(clientDataLocal);
        } catch (e) {
            console.log("USER NOT READY");
            console.log("error", e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (user !== undefined) getBalance();
        // eslint-disable-next-line
    }, [user]);

    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<Outlet />}>
                    <Route
                        index
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        {!subscription ? (
                                            <Tasks
                                                updateProfiles={setProfiles}
                                                aProfile={activeProfile}
                                                updateAProfile={
                                                    setActiveProfile
                                                }
                                                profiles={profiles}
                                                balance={balance}
                                                updateBalance={setBalance}
                                            />
                                        ) : (
                                            <Campaigns
                                                balance={balance}
                                                updateBalance={setBalance}
                                                subscription={subscription}
                                                clientData={clientData}
                                            />
                                        )}
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/analytics"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <Analytics
                                            balance={balance}
                                            updateBalance={setBalance}
                                            subscription={subscription}
                                            clientData={clientData}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/tasks"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <Tasks
                                            updateProfiles={setProfiles}
                                            aProfile={activeProfile}
                                            updateAProfile={setActiveProfile}
                                            profiles={profiles}
                                            balance={balance}
                                            updateBalance={setBalance}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/campaigns"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <Campaigns
                                            balance={balance}
                                            updateBalance={setBalance}
                                            subscription={subscription}
                                            clientData={clientData}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/newtargetedcampaign"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <NewTargetedCampaign
                                            balance={balance}
                                            updateBalance={setBalance}
                                            subscription={subscription}
                                            clientData={clientData}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/updatetargetedcampaign/:targetedcampaignId"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <UpdateTargetedCampaign
                                            balance={balance}
                                            updateBalance={setBalance}
                                            subscription={subscription}
                                            clientData={clientData}
                                            profiles={profiles}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/newtestcampaign"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <NewTestCampaign
                                            balance={balance}
                                            updateBalance={setBalance}
                                            subscription={subscription}
                                            clientData={clientData}
                                            refreshClient={getBalance}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/history"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <CampaignHistory
                                            aProfile={activeProfile}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/listings"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <Listings
                                            balance={balance}
                                            updateBalance={setBalance}
                                            subscription={subscription}
                                            clientData={clientData}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/listings/new"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <ListingForm mode="new" clientData={clientData} />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/listings/edit/:slug"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <ListingForm mode="edit" clientData={clientData} />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/brand-cookbook"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <BrandCookbook profiles={profiles} accessLevel={accessLevel}  clientData={clientData}/>
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/influencer-cookbook"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <InfluencerCookbook profiles={profiles} accessLevel={accessLevel} clientData={clientData}/>
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/earnings"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <Earnings profiles={profiles} accessLevel={accessLevel} />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/billing"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <Billing
                                            clientData={clientData}
                                            updateClientData={setClientData}
                                            updateSubscription={setSubscription}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/addProfile"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <AddProfile
                                            updateProfiles={setProfiles}
                                            aProfile={activeProfile}
                                            updateAProfile={setActiveProfile}
                                            clientData={user?.username}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />

                    <Route
                        path="/dashboard"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        {accessLevel === "admin" ? (
                                            <Dashboard
                                                updateProfiles={setProfiles}
                                                aProfile={activeProfile}
                                                updateAProfile={
                                                    setActiveProfile
                                                }
                                            />
                                        ) : (
                                            <>Access denied</>
                                        )}
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/dashboard/marketing-report"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        {accessLevel === "admin" ? (
                                            <MarketingReport
                                                updateProfiles={setProfiles}
                                                aProfile={activeProfile}
                                                updateAProfile={
                                                    setActiveProfile
                                                }
                                            />
                                        ) : (
                                            <>Access denied</>
                                        )}
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/dashboard/campaigns-report"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        {accessLevel === "admin" ? (
                                            <CampaignsReport
                                                profiles={profiles}
                                            />
                                        ) : (
                                            <>Access denied</>
                                        )}
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/dashboard/users-report"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        {accessLevel === "admin" ? (
                                            <UsersReport
                                                updateProfiles={setProfiles}
                                                aProfile={activeProfile}
                                                updateAProfile={
                                                    setActiveProfile
                                                }
                                            />
                                        ) : (
                                            <>Access denied</>
                                        )}
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/dashboard/payouts-report"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <PayoutsReport
                                            profiles={profiles}
                                            accessLevel={accessLevel}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/dashboard/most-active-users-report"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        {accessLevel === "admin" ? (
                                            <MostActiveUsersReport />
                                        ) : (
                                            <>Access denied</>
                                        )}
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/dashboard/active-platform-users-report"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        {accessLevel === "admin" ? (
                                            <MostActivePlatformUsersReport />
                                        ) : (
                                            <>Access denied</>
                                        )}
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />

                    <Route
                        path="/settings"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <Settings
                                            updateProfiles={setProfiles}
                                            aProfile={activeProfile}
                                            updateAProfile={setActiveProfile}
                                            profiles={profiles}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/settings/:profileId"
                        element={
                            <RequireAuth>
                                {isLoading ? (
                                    <Loader variation="linear" />
                                ) : (
                                    <Layout
                                        accessLevel={accessLevel}
                                        profiles={profiles}
                                        balance={balance}
                                        updateBalance={setBalance}
                                        aProfile={activeProfile}
                                        updateAProfile={setActiveProfile}
                                        clientData={clientData}
                                    >
                                        <SpecificProfile
                                            updateProfiles={setProfiles}
                                            aProfile={activeProfile}
                                            updateAProfile={setActiveProfile}
                                        />
                                    </Layout>
                                )}
                            </RequireAuth>
                        }
                    />

                    <Route
                        path="/signin"
                        element={
                            <SimpleLayout>
                                <Register initialState="signIn" />
                            </SimpleLayout>
                        }
                    />
                    <Route
                        path="/signup"
                        element={
                            <SimpleLayout>
                                <Register initialState="signUp" />
                            </SimpleLayout>
                        }
                    />
                    <Route
                        path="/passwordrestore"
                        element={
                            <SimpleLayout>
                                <Register initialState="resetPassword" />
                            </SimpleLayout>
                        }
                    />
                    <Route path="*" element={<Navigate to="/" />} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
}

export default function App() {
    return (
        <ThemeProvider theme={theme}>
            <Authenticator.Provider>
                <MyRoutes />
            </Authenticator.Provider>
        </ThemeProvider>
    );
}
